import { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Link } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

interface Props {
  initialTitle?: string | null;
  initialBody?: string;
  prompt: string;
  nativePrompt: string;
  learningLanguage: string;
  nativeLanguage: string;
  onSave: (title: string | undefined, body: string) => void;
  isSaving?: boolean;
}

export const EntryEditor = ({
  initialTitle,
  initialBody = '',
  prompt,
  nativePrompt,
  learningLanguage,
  nativeLanguage,
  onSave,
  isSaving,
}: Props) => {
  const [title, setTitle] = useState(initialTitle ?? '');
  const [body, setBody] = useState(initialBody);
  const [isDirty, setIsDirty] = useState(false);
  const [showNativePrompt, setShowNativePrompt] = useState(false);

  useEffect(() => {
    setTitle(initialTitle ?? '');
    setBody(initialBody);
    setIsDirty(false);
  }, [initialTitle, initialBody]);

  const displayedPrompt = showNativePrompt
    ? `(${nativeLanguage}) ${nativePrompt}`
    : `(${learningLanguage}) ${prompt}`;

  const handleBodyChange = (value: string) => {
    setBody(value);
    setIsDirty(value !== initialBody || title !== (initialTitle ?? ''));
  };

  const handleSave = () => {
    onSave(title.trim() || undefined, body);
    setIsDirty(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', pt: 1 }}>
      <TextField
        label="Title (optional)"
        value={title}
        onChange={e => { setTitle(e.target.value); setIsDirty(true); }}
        fullWidth
        variant="outlined"
        size="small"
      />
      <TextField
        multiline
        minRows={12}
        maxRows={24}
        value={body}
        onChange={e => handleBodyChange(e.target.value)}
        placeholder={displayedPrompt}
        fullWidth
        variant="outlined"
        inputProps={{ style: { fontFamily: 'inherit', fontSize: '1rem', lineHeight: 1.7 } }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => setShowNativePrompt(v => !v)}
          underline="hover"
          color="text.secondary"
        >
          {showNativePrompt
            ? `Switch prompt to ${learningLanguage}`
            : `Switch prompt to ${nativeLanguage}`}
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isDirty && (
            <Typography variant="caption" color="warning.main">Unsaved changes</Typography>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
